/**
 * @file AssetUpload.js
 * @description Component for uploading or selecting assets within a container.
 * Supports multiple file types including images and videos, displays uploaded assets,
 * and manages the asset upload process with API integration.
 */
import React, { useState, useRef, useEffect, useContext } from 'react';
import './AssetUpload.css';
import { useEditor, EditorContent } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import {
    Color,
    FontFamily,
    FontSize,
    TextStyle,
} from '@tiptap/extension-text-style';
import { ListItem } from '@tiptap/extension-list';
import { Placeholder } from '@tiptap/extensions';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import {
    Table,
    TableCell,
    TableHeader,
    TableRow,
} from '@tiptap/extension-table';
import ImageCarousel from '../multimedia-component/ImageCarousel';
import Video from '../video-component/Video';
import { fetchAssetByID } from '../../services/clientService';
import { FileDrop } from '../helpers/FileDrop';
import Toast from '../ui/toasts/Toast';
import { isValidFileTypeAndSize } from '../../utils/validate';
import {
    createConversation,
    createMessageWithAsset,
    deleteAssetWithMessage,
    getAllConversations,
    getAllMessages,
} from '../../services/conversationService';
import {
    ModalTypes,
    useAuthContext,
    ModalContext,
    useUploadContext,
} from '../../store/Contexts';
import NotFoundBox from '../ui/no_data_indicators/NotFoundBox';
import '../helpers/FileDrop.css';
import { submitContainerAssetUpload } from '../../services/assetUploadPartService';
import { logEvent } from '../../services/loggingService';
import {
    formatFileSize,
    getFileCategory,
    getVideoRuntime,
    getAudioLength,
    getImageDimensions,
    sanitizeFileName,
} from '../helpers/FileUtils';
import { EventDataTypeEnum, LogEventType } from '../../utils/data/enums';
import ResizableImage from '../../utils/tiptap/ResizableImageExtension';
import { VideoNode } from '../new-portal-page/VideoNode';
import { QRCodeNode } from '../new-portal-page/QRCodeNode';
import DivParagraph from '../new-portal-page/DivParagraph';
import Emoji from '../../utils/tiptap/EmojiExtension';
import { PortalLinkNode } from '../new-portal-page/PortalLinkNode';
import { useMenuLinkNavigation } from '../../hooks/useMenuLinkNavigation';
import { getText } from '../../i18n';

function isAssetAnchor(anchor) {
    if (!anchor) {
        return false;
    }

    if (anchor.getAttribute('data-link-type') === 'asset') {
        return true;
    }

    const href = anchor.getAttribute('href') || '';
    return /\/files\/asset\//i.test(href);
}

const AssetUpload = ({
    containerId,
    containerTitle,
    containerAssetUploadPart,
    onAssetUploadComplete,
    dueDate,
    assets,
    text,
    acceptedFileTypes,
    portalName,
    portalId,
    navigationStack,
    editMode = false,
    isPreview = false,
    isViewLive = false,
    onShowPortalView,
    url,
}) => {
    const [videoUrl, setVideoUrl] = useState(url);
    const [images, setImages] = useState([]);
    const [file, setFile] = useState();
    const [isAddFromAssetsChecked, setIsAddFromAssetsChecked] = useState(false);
    const [isUploadFromDeviceChecked, setIsUploadFromDeviceChecked] =
        useState(false);
    const { showModal } = useContext(ModalContext);
    const { enqueueUpload } = useUploadContext();
    const { user } = useAuthContext();
    const [conversationId, setConversationId] = useState(0);
    const [isAssetUploadCompleted, setIsAssetUploadCompleted] = useState(false);
    const [daysSinceOrToDueDate, setDaysSinceOrToDueDate] = useState(0);
    const [uploadedAssetName, setUploadedAssetName] = useState('');
    const [uploadedAssetId, setUploadedAssetId] = useState(null);
    const isMountedRef = useRef(true);

    useEffect(
        () => () => {
            isMountedRef.current = false;
        },
        [],
    );

    useEffect(() => {
        if (containerId === null) return;
        getAllConversations('container', containerId)
            .then(conversations => {
                let existingConversationId = conversations[0]?.id;
                if (!existingConversationId) {
                    const newConversation = {
                        topicId: containerId,
                        topicType: 'container',
                    };
                    createConversation(newConversation)
                        .then(conversation => {
                            existingConversationId = conversation[0].id;
                        })
                        .catch(error => {
                            console.error('createConversation failed:', error);
                        });
                }
                setConversationId(existingConversationId);

                getAllMessages(existingConversationId, user.jwt.nameid)
                    .then(messages => {
                        if (messages?.length === 0)
                            setIsAssetUploadCompleted(false);
                        else {
                            const latestMessage = messages.reduce(
                                function (prev, current) {
                                    return prev && prev.id > current.id
                                        ? prev
                                        : current;
                                },
                            );
                            setIsAssetUploadCompleted(
                                latestMessage.contents === 'false' || null
                                    ? false
                                    : true,
                            );
                            if (
                                latestMessage.contents !== 'false' ||
                                latestMessage.contents !== null
                            ) {
                                setUploadedAssetId(latestMessage.contents);
                            }
                        }
                    })
                    .catch(error => {
                        console.error('createConversation failed:', error);
                    });
            })
            .catch(error => {
                console.error('getAllConversations failed:', error);
            });
    }, []);

    const getUploadedAssetName = async () => {
        try {
            const fetchedAsset = await fetchAssetByID(uploadedAssetId, null);
            setUploadedAssetName(fetchedAsset.name);
        } catch (error) {
            console.error('Failed to fetch asset', error);
            Toast.error(getText('msg_b9f02f25f059'));
            setUploadedAssetName('Unknown asset name');
        }
    };

    useEffect(() => {
        if (
            isAssetUploadCompleted &&
            uploadedAssetId !== null &&
            uploadedAssetId !== 'false'
        ) {
            getUploadedAssetName();
        }
    }, [isAssetUploadCompleted, uploadedAssetId]);

    const onUploadClick = () => {
        if (editMode) {
            return;
        }

        const fileTypeAndSizeValidation = isValidFileTypeAndSize(file);
        if (fileTypeAndSizeValidation) {
            return Toast.error(fileTypeAndSizeValidation);
        }

        const newAssetName =
            portalName +
            ' - ' +
            containerTitle +
            ' - ' +
            user.last_name +
            ', ' +
            user.first_name +
            ' (' +
            user.id +
            ')';

        if (!isPreview && !isViewLive) {
            const fileToUpload = sanitizeFileName(file);

            //Runs to completion in the background regardless of this component's lifecycle
            enqueueUpload(fileToUpload.name, uploadOptions =>
                submitContainerAssetUpload({
                    file: fileToUpload,
                    assetName: newAssetName,
                    containerId,
                    portalId,
                    containerAssetUploadPart,
                    uploadOptions,
                }),
            )
                .then(({ id, name }) => {
                    if (!isMountedRef.current) return;
                    setUploadedAssetName(name);
                    setUploadedAssetId(id);
                    setIsAssetUploadCompleted(true);
                    onAssetUploadComplete(true);
                    setFile(null);
                })
                .catch(error => {
                    console.error('An error occurred:', error);
                });

            //Tracker owns success/failure feedback from here; just confirm the queue
            Toast.info(getText('msg_9309271e92ff'));
            return;
        }

        setUploadedAssetName(newAssetName);
        setUploadedAssetId(null);
        setIsAssetUploadCompleted(true);
        onAssetUploadComplete(true);
        setFile(null);
        if (!isViewLive) {
            Toast.success(getText('msg_ef89279f41c3'));
            logEvent(
                LogEventType.AssetUploaded,
                `Uploaded asset ${newAssetName}`,
                portalId,
                containerId,
            );
        }
    };

    const onAddFromAssets = async asset => {
        if (editMode) {
            return;
        }

        if (!isPreview && !isViewLive) {
            //Create message
            const newMessage = {
                assetUploadPartId: containerAssetUploadPart.id,
                assetId: asset.id,
            };
            await createMessageWithAsset(newMessage); //Wait for the message to be created
            Toast.success(getText('msg_b9f3cbd3f113'));
            logEvent(
                LogEventType.AssetUploaded,
                `Uploaded asset ${asset.name}`,
                {
                    contextDataTypeId: EventDataTypeEnum.Container,
                    contextId: containerId,
                },
            );
        }

        setIsAssetUploadCompleted(true);
        onAssetUploadComplete(true);
        setIsAddFromAssetsChecked(false);
        setUploadedAssetName(asset.name);
        setUploadedAssetId(asset.id);
        setFile(null);
        if (!isViewLive) {
            Toast.success(getText('msg_b9f3cbd3f113'));
        }
        if (!isViewLive) {
            logEvent(
                LogEventType.AssetUploaded,
                `Uploaded asset ${asset.name}`,
                {
                    contextDataTypeId: EventDataTypeEnum.Container,
                    contextId: containerId,
                },
            );
        }
        setIsAddFromAssetsChecked(false);
    };

    const onRemoveAsset = () => {
        if (editMode) {
            return;
        }

        if (!isPreview && !isViewLive) {
            deleteAssetWithMessage(
                containerAssetUploadPart.id,
                uploadedAssetId,
                conversationId,
            )
                .then(() => {
                    setIsAssetUploadCompleted(false);
                    onAssetUploadComplete(false);
                })
                .catch(error => {
                    console.error('createMessage failed:', error);
                });
        } else {
            setIsAssetUploadCompleted(false);
            onAssetUploadComplete(false);
        }
    };

    useEffect(() => {
        if (!isAddFromAssetsChecked || editMode) {
            return;
        }

        setIsUploadFromDeviceChecked(false);
        showModal({
            type: ModalTypes.ADD_FROM_ASSETS,
            title: getText('msg_842b4a662bd0'),
            onYes: asset => onAddFromAssets(asset),
            onNo: () => setIsAddFromAssetsChecked(false),
            onHide: () => setIsAddFromAssetsChecked(false),
            noButtonText: getText('msg_77dfd2135f4d'),
            yesButtonText: getText('msg_61cc55aa0453'),
            acceptedFileTypes: acceptedFileTypes.map(type => type.fileType),
        });
    }, [acceptedFileTypes, editMode, isAddFromAssetsChecked]);

    useEffect(() => {
        if (dueDate) {
            setDaysSinceOrToDueDate(calculateDaysSinceDate(dueDate));
        }
    }, [dueDate]);

    const calculateDaysSinceDate = dateString => {
        const inputDate = new Date(dateString);
        const currentDate = new Date();
        const differenceInMilliseconds = currentDate - inputDate;
        const millisecondsInADay = 1000 * 60 * 60 * 24;
        return Math.floor(differenceInMilliseconds / millisecondsInADay);
    };

    const handleFileChange = fileValue => {
        if (editMode) {
            return;
        }

        if (fileValue) {
            const allowedFileTypes = acceptedFileTypes.map(
                type => type.fileType,
            );
            const fileExtension = fileValue.name.split('.').pop().toLowerCase();

            if (allowedFileTypes.includes(fileExtension)) {
                setFile(sanitizeFileName(fileValue));
            } else {
                Toast.error(
                    `Invalid file type. Allowed types are: ${allowedFileTypes.join(', ')}`,
                );
            }
        } else {
            setFile(null);
        }
    };

    const previousAssetsRef = useRef();

    useEffect(() => {
        if (previousAssetsRef.current !== assets) {
            const loadAllAssets = async () => {
                if (assets.length > 0) {
                    const firstAsset = assets[0];
                    const fetchedFirstAsset = await fetchAssetByID(
                        firstAsset.assetId,
                        null,
                    );
                    if (
                        ['jpg', 'jpeg', 'png', 'svg'].includes(
                            fetchedFirstAsset.fileType,
                        )
                    ) {
                        for (const asset of assets) {
                            await loadContainerAssets(asset.assetId);
                            setVideoUrl(null);
                        }
                    } else if (
                        ['mov', 'mp4'].includes(fetchedFirstAsset.fileType)
                    ) {
                        setVideoUrl(fetchedFirstAsset.url);
                        setImages([]);
                    }
                } else if (url === null || url === undefined || url === '') {
                    setVideoUrl(null);
                    setImages([]);
                }
            };
            loadAllAssets();
            previousAssetsRef.current = assets;
        }
    }, [assets]);

    useEffect(() => {
        setVideoUrl(url);
        setImages([]);
    }, [url]);

    const loadContainerAssets = async assetId => {
        try {
            const fetchedAsset = await fetchAssetByID(assetId, null);
            setImages(prevImages => {
                if (
                    !prevImages.includes(fetchedAsset.url) &&
                    !text?.includes(fetchedAsset.url)
                ) {
                    return [...prevImages, fetchedAsset.url];
                }
                return prevImages;
            });
        } catch (error) {
            console.error('Failed to fetch asset', error);
        }
    };

    const CustomTableCell = TableCell.extend({
        addAttributes() {
            return {
                ...this.parent?.(),
                backgroundColor: {
                    default: null,
                    parseHTML: element =>
                        element.getAttribute('data-background-color'),
                    renderHTML: attributes => {
                        return {
                            'data-background-color': attributes.backgroundColor,
                            style: `background-color: ${attributes.backgroundColor}`,
                        };
                    },
                },
            };
        },
    });

    const editor = useEditor({
        extensions: [
            Color.configure({ types: [TextStyle.name, ListItem.name] }),
            TextStyle.configure({ types: [ListItem.name] }),
            FontFamily.configure({
                types: ['textStyle'],
            }),
            FontSize,
            PortalLinkNode.configure(
                editMode
                    ? {
                          interactive: false,
                          onBlockedClick: null,
                      }
                    : isPreview && typeof onShowPortalView === 'function'
                      ? {
                            interactive: false,
                            onBlockedClick: portalIdValue => {
                                onShowPortalView(
                                    Number(portalIdValue),
                                    navigationStack,
                                );
                            },
                        }
                      : {
                            interactive: true,
                            navigationStack: navigationStack,
                            parentPortalId: portalId,
                            ...(isViewLive && { routePrefix: '/view-live' }),
                        },
            ),
            StarterKit.configure({
                // StarterKit v3 bundles these; keep the explicitly configured ones.
                link: false,
                underline: false,
                trailingNode: false,
                bulletList: {
                    keepMarks: true,
                    keepAttributes: false,
                },
                orderedList: {
                    keepMarks: true,
                    keepAttributes: false,
                },
            }),
            Placeholder.configure({
                placeholder: getText('msg_8b4539a50a3b'),
            }),
            Underline,
            TextAlign.configure({
                types: ['heading', 'paragraph'],
            }),
            Link.configure({
                protocols: ['ftp', 'mailto'],
                HTMLAttributes: {
                    class: '',
                },
                openOnClick: editMode ? false : true,
            }),
            ResizableImage,
            Table.configure({
                resizable: true,
            }),
            TableRow,
            TableHeader,
            TableCell,
            CustomTableCell,
            Emoji,
            VideoNode,
            QRCodeNode,
            DivParagraph,
        ],
        content: normalizeHtmlForEditorLoad(text),
        editable: false,
    });

    if (!editor) {
        return null;
    }

    function normalizeHtmlForEditorLoad(html) {
        if (typeof html !== 'string' || !html) return html || '';

        return html.replace(/<div>\s*<br\s*\/?>\s*<\/div>/gi, '<div></div>');
    }

    useEffect(() => {
        if (editor && editor.getHTML() !== text) {
            const normalizedText = normalizeHtmlForEditorLoad(text);
            editor.commands.setContent(normalizedText, { emitUpdate: false });
        }
    }, [text]);

    useMenuLinkNavigation(editor, { isPreview, isViewLive });

    function handleContentClickCapture(event) {
        if (editMode) {
            return;
        }

        const assetAnchor = event.target.closest('a');

        if (!isAssetAnchor(assetAnchor)) {
            return;
        }

        const href = assetAnchor.getAttribute('href');
        if (!href) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        showModal({
            type: ModalTypes.ASSET_VIEW,
            title: assetAnchor.textContent?.trim() || 'Asset Preview',
            text: href,
        });
    }

    const hasImages = images.length > 0;
    const hasVideo = !!videoUrl;
    const hasMedia = hasImages || hasVideo;

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
                width: '100%',
            }}
        >
            {hasImages && <ImageCarousel images={images} />}

            {hasVideo && (
                <Video
                    url={videoUrl}
                    caption={''}
                    id={containerId}
                    name={containerTitle}
                    isInteractive={!editMode}
                />
            )}

            {text !== null && text !== undefined && text !== '' ? (
                <div
                    style={{
                        margin: hasMedia ? '0 20px' : '0px',
                    }}
                    onClickCapture={handleContentClickCapture}
                >
                    <EditorContent
                        editor={editor}
                        className={
                            editMode
                                ? 'custom-readonly-editor disable-links'
                                : 'custom-readonly-editor'
                        }
                    />
                </div>
            ) : null}

            {daysSinceOrToDueDate > 0 && !isAssetUploadCompleted && (
                <div
                    style={{
                        width: 'fit-content',
                        margin: hasMedia ? '-40px 20px' : '-40px 0px', // Account for inherit margin in the NotFoundBox component
                    }}
                >
                    <NotFoundBox
                        backgroundColor="#f0f4ff"
                        title={getText('msg_fbfbcf0d601f')}
                        description={getText('msg_ccf59d6f7020')}
                        icon="calendar-x"
                        width="100%"
                    />
                </div>
            )}

            <div
                style={{
                    pointerEvents: editMode ? 'none' : 'auto',
                    margin: hasMedia ? '0 20px' : '0px',
                }}
            >
                {daysSinceOrToDueDate <= 0 && !isAssetUploadCompleted && (
                    <>
                        <div>
                            <div
                                className="d-flex justify-content-center mb-4"
                                style={{ flexDirection: 'column', gap: '24px' }}
                            >
                                <button
                                    style={{ width: '100%' }}
                                    className={`button-base ${isAddFromAssetsChecked ? 'ing-button-primary-fit-content' : 'button-secondary'}`}
                                    onClick={() => {
                                        if (!editMode) {
                                            setIsAddFromAssetsChecked(true);
                                        }
                                    }}
                                >
                                    {getText('msg_27eb0a10db5c')}
                                </button>
                                <button
                                    style={{ width: '100%' }}
                                    className={`button-base ${isUploadFromDeviceChecked ? 'ing-button-primary-fit-content' : 'button-secondary'}`}
                                    onClick={() => {
                                        if (!editMode) {
                                            setIsUploadFromDeviceChecked(true);
                                        }
                                    }}
                                >
                                    {getText('msg_72204a31fe06')}
                                </button>
                            </div>
                            {isUploadFromDeviceChecked && (
                                <FileDrop
                                    file={file}
                                    onChange={handleFileChange}
                                />
                            )}
                        </div>
                        <button
                            className="button-base ing-button-primary-fit-content"
                            style={{
                                alignSelf: 'end',
                                paddingLeft: '50px',
                                paddingRight: '50px',
                            }}
                            onClick={() => {
                                onUploadClick();
                            }}
                        >
                            {getText('msg_8bdf057f91e7')}
                        </button>
                    </>
                )}

                {isAssetUploadCompleted && (
                    <div className="form-file-drop">
                        <div className="form-file-dropped">
                            <div className="form-file-dropped-status">
                                {getText('msg_81b4ce912e2b')}
                            </div>
                            <div className="form-file-dropped-filename">
                                {uploadedAssetName}
                                <i
                                    className="bi bi-x form-file-dropped-x-icon"
                                    onClick={() => onRemoveAsset()}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AssetUpload;
